import { useCallback, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/lib/hooks/use-toast';
import { importTrackingService } from '@/features/import-tracking/services/import-tracking-service';
import { ProformaUpload } from './proforma-upload';
import { ProformaReviewTable } from './proforma-review-table';
import type { ProformaExtraction, ProformaLine } from '../types/trade.types';

/**
 * Carga masiva de productos desde la proforma del proveedor.
 *
 * Tres pasos: subir → revisar → confirmar. El paso del medio no se puede
 * saltear, que es justamente el punto: la IA propone, la persona decide.
 *
 * Al confirmar se manda **una** petición con todas las líneas y el backend las
 * escribe en una transacción: todo o nada. Antes era una petición por línea, y con
 * 520 productos eso dejaba la orden a medias si algo se cortaba.
 */

export interface ProformaImportPanelProps {
  orderId: string;
  /** Se llama cuando terminó de importar, para recargar la lista. */
  onImported: () => void;
}

export function ProformaImportPanel({ orderId, onImported }: ProformaImportPanelProps) {
  const { toast } = useToast();
  const [extraction, setExtraction] = useState<ProformaExtraction | null>(null);
  /**
   * El archivo original, que se guarda como documento al confirmar.
   *
   * Se importan 520 líneas de un Excel o un PDF y hasta ahora el archivo
   * desaparecía. Si en tres meses el cliente reclama que un precio no era ese, sin
   * el original no hay con qué defenderse.
   */
  const [sourceFile, setSourceFile] = useState<File | null>(null);

  const reset = useCallback(() => {
    setExtraction(null);
    setSourceFile(null);
  }, []);

  const handleExtracted = useCallback((next: ProformaExtraction, file: File) => {
    setExtraction(next);
    setSourceFile(file);
  }, []);

  /**
   * Confirma la importación en **una** petición.
   *
   * Antes era una petición HTTP por línea. Con las 520 de una proforma real de
   * Gree eso son 520 idas y vueltas: cerca de un minuto de spinner, y si el usuario
   * cerraba la pestaña en la línea 300 la orden quedaba con 300 productos y 220
   * perdidos, sin forma de saber cuáles faltaban ni de reintentar sin duplicar.
   *
   * Ahora el backend lo hace en una transacción: todo o nada. Si una línea falla,
   * no entra ninguna y el mensaje dice cuál — la persona corrige esa y vuelve a
   * mandar, con la orden intacta.
   */
  const handleConfirm = useCallback(
    async (lines: ProformaLine[]) => {
      // El botón de la tabla de revisión ya se bloquea solo mientras esto corre.
      try {
        const result = await importTrackingService.addOrderItemsBulk(
          orderId,
          lines.map((line) => ({
            description: line.description,
            hsCode: line.hsCode ?? undefined,
            quantity: line.quantity,
            unitOfMeasure: 'PCS',
            unitFobPrice: line.unitPrice,
            weightKg: line.unitWeightKg ?? undefined,
            lengthCm: line.lengthCm ?? undefined,
            widthCm: line.widthCm ?? undefined,
            heightCm: line.heightCm ?? undefined,
            /*
              El id de la partida es lo que importa: con él la alícuota se
              resuelve contra el arancel vigente en cada recálculo. `dutyRate`
              va como respaldo para poder leer la fila, pero no manda.
            */
            tariffHeadingId: line.tariffHeadingId ?? null,
            dutyRate: line.gaRate ?? undefined,
          }))
        );

        /*
          El archivo original se adjunta después de que los productos entraron.

          En ese orden a propósito: si falla la subida del archivo, los productos ya
          están y no se pierde el trabajo de revisión. Al revés —adjuntar primero—
          dejaría un documento huérfano si la importación se cae.

          Y no rompe el flujo si falla: se avisa y listo. Perder la trazabilidad es
          malo, pero no tanto como hacerle revisar 520 líneas de nuevo.
        */
        let attached = true;
        if (sourceFile) {
          try {
            await importTrackingService.uploadDocument(
              orderId,
              sourceFile,
              'COMMERCIAL_INVOICE',
              `Proforma del proveedor — ${sourceFile.name}`
            );
          } catch {
            attached = false;
          }
        }

        toast({
          title: `${result.imported} producto(s) importados`,
          description:
            result.imported !== result.requested
              ? `Se pidieron ${result.requested} y entraron ${result.imported}. Revisá la lista.`
              : attached
                ? 'Ya se pueden estibar. La proforma quedó guardada en Documentos.'
                : 'Ya se pueden estibar. No se pudo guardar el archivo original — subilo a mano en Documentos.',
          variant: result.imported === result.requested && attached ? 'success' : 'warning',
        });

        reset();
        onImported();
      } catch (err) {
        // No se cierra el panel: la tabla revisada sigue en pantalla para que la
        // persona corrija la línea que el backend nombró y vuelva a confirmar.
        toast({
          title: 'No se importó ninguno',
          description:
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
            'Ocurrió un error al importar. La orden quedó sin cambios.',
          variant: 'destructive',
        });
      }
    },
    [orderId, sourceFile, onImported, reset, toast]
  );

  /*
    La revisión va en una capa propia, no en línea.

    Este componente vive en la fila del encabezado de Productos, al lado de
    "Agregar Producto". La tabla de revisión es de ancho completo, así que
    renderizada ahí empujaba al botón fuera de la tarjeta — es el desborde que se
    veía en pantalla.

    Y aparte corresponde: revisar 520 líneas necesita el ancho de la ventana, no
    el hueco de un encabezado. Mientras se revisa, agregar un producto a mano no
    es la acción siguiente.
  */
  return (
    <>
      <ProformaUpload mode="button" onExtracted={handleExtracted} />

      {extraction && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Revisar la proforma antes de importar"
        >
          <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
            <div>
              <h3 className="text-sm font-semibold">Revisar antes de importar</h3>
              <p className="text-xs text-muted-foreground">Nada se guarda hasta que confirmes.</p>
            </div>
            <Button size="sm" variant="ghost" onClick={reset} aria-label="Cerrar">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* La tabla scrollea acá dentro, no la página. */}
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <div className="mx-auto max-w-6xl">
              <ProformaReviewTable
                extraction={extraction}
                onConfirm={handleConfirm}
                onCancel={reset}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
